import type { AxiosError } from "axios";
import http from "../../api/apiClient";
import type { BankAccount, CurrentSchoolYear, FamilySummary, Parent, ParentCreatePayload, ParentUpdatePayload } from "./types";
import { fetchCurrentSchoolYear } from "../../utils/fetchSchoolYears";

const isAxiosStatus = (error: unknown, statuses: number[]): boolean => {
  const status = (error as AxiosError | undefined)?.response?.status;
  return typeof status === "number" && statuses.includes(status);
};

export const fetchParents = async (): Promise<Parent[]> => {
  const { data } = await http.get<Parent[]>("/parents");
  return Array.isArray(data) ? data : [];
};

export const fetchParent = async (id: string): Promise<Parent> => {
  const { data } = await http.get<Parent>(`/parents/${id}`);
  return data;
};

export const createParent = async (payload: ParentCreatePayload): Promise<Parent> => {
  const { data } = await http.post<Parent>("/parents", payload);
  return data;
};

export const updateParent = async (id: string, payload: ParentUpdatePayload): Promise<Parent> => {
  const { data } = await http.put<Parent>(`/parents/${id}`, payload);
  return data;
};

export const deleteParent = async (id: string): Promise<void> => {
  await http.delete(`/parents/${id}`);
};

export const fetchFamiliesForParent = async (id: string): Promise<FamilySummary[]> => {
  const { data } = await http.get<FamilySummary[]>("/families", { params: { parentId: id } });
  return Array.isArray(data) ? data : [];
};

export const fetchFamilies = async (): Promise<FamilySummary[]> => {
  const { data } = await http.get<FamilySummary[]>("/families");
  return Array.isArray(data) ? data : [];
};

export const fetchBankAccountForParent = async (id: string): Promise<BankAccount | null> => {
  try {
    const { data } = await http.get<BankAccount | null>(`/bank-accounts/by-parent/${id}`);
    return data || null;
  } catch (error) {
    if (isAxiosStatus(error, [403, 404])) return null;
    throw error;
  }
};

export const fetchCurrentSchoolYearIfAllowed = async (): Promise<CurrentSchoolYear | null> => {
  try {
    return (await fetchCurrentSchoolYear()) as CurrentSchoolYear | null;
  } catch (error) {
    if (isAxiosStatus(error, [403])) return null;
    throw error;
  }
};

export const fetchChildClassroomHistory = async (childId: string): Promise<unknown[]> => {
  const { data } = await http.get<unknown[]>(`/child-classrooms/classrooms-for-child/${childId}`);
  return Array.isArray(data) ? data : [];
};

export const recomputeNewsletterForFamily = async (familyId: string): Promise<void> => {
  await http.post(`/admin/newsletter/recompute/family/${familyId}`);
};

export const unlinkParentKeycloak = async (parentId: string): Promise<void> => {
  await http.post(`/parents/${parentId}/unlink-keycloak`);
};
